import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Lightning, ChartLineUp, Coins } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const fmt = (n) => (n == null ? "--" : `$${Number(n).toFixed(2)}`);

// Format Keepa refill ETA — e.g. 56000ms → "56s", 240000ms → "4m"
function formatRefillIn(ms) {
  if (ms == null || ms <= 0) return null;
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  return `${Math.round(ms / 60000)}m`;
}

function tokenTone(tokens) {
  if (tokens == null) return "muted";
  if (tokens <= 0) return "danger";
  if (tokens < 50) return "warn";
  if (tokens < 150) return "ok";
  return "fresh";
}

export default function TopNav() {
  const [stats, setStats] = useState(null);
  const [ticker, setTicker] = useState([]);
  const [tokens, setTokens] = useState({ left: null, refill_rate: null, refill_in_ms: null });
  const [ebayCredits, setEbayCredits] = useState({ left: null, cached: null });
  const location = useLocation();

  useEffect(() => {
    api.dashboardStats().then(d => {
      setStats(d);
      const t = (d.top_by_roi || []).concat(d.top_by_score || []);
      setTicker(t);
    }).catch(() => {});
  }, [location.pathname]);

  // Live Keepa-token poller — every 30s, plus an immediate fetch on mount.
  useEffect(() => {
    let cancelled = false;
    const fetchTokens = async () => {
      try {
        const r = await api.asinResolverStats();
        if (cancelled) return;
        setTokens({
          left: r?.keepa_tokens_left,
          refill_rate: r?.keepa_refill_rate,
          refill_in_ms: r?.keepa_refill_in_ms,
        });
      } catch {}
    };
    fetchTokens();
    const id = setInterval(fetchTokens, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Live SerpApi credit poller — every 60s (less frequent than Keepa since
  // this only changes when we actually spend a credit).
  useEffect(() => {
    let cancelled = false;
    const fetchEbay = async () => {
      try {
        const r = await api.ebayStatus();
        if (cancelled) return;
        setEbayCredits({
          left: r?.searches_left,
          cached: r?.cached_sets_last_24h,
        });
      } catch {}
    };
    fetchEbay();
    const id = setInterval(fetchEbay, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const tone = tokenTone(tokens.left);
  const toneCls = {
    fresh:  "border-[#00A850]/40 bg-[#00A850]/10 text-[#00A850]",
    ok:     "border-[#E3000B]/40 bg-[#E3000B]/10 text-[#E3000B]",
    warn:   "border-[#FF8C00]/40 bg-[#FF8C00]/10 text-[#FF8C00]",
    danger: "border-[#FF3333]/40 bg-[#FF3333]/10 text-[#FF3333] animate-pulse",
    muted:  "border-gray-300 bg-gray-100 text-gray-500",
  }[tone];
  const refillEta = formatRefillIn(tokens.refill_in_ms);

  return (
    <header className="border-b border-[#E4E4E0] bg-[#FFFFFF] sticky top-0 z-40" data-testid="top-nav">
      <div className="h-1 w-full" style={{ background: "linear-gradient(90deg, #E3000B 0 25%, #FFD500 25% 50%, #0055BF 50% 75%, #00A850 75%)" }} />
      <div className="flex items-center justify-between px-4 h-12">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
          <img src="/brand/offers4bricks-logo@1000.webp" alt="OFFERS4BRICKS" className="h-7 w-auto" draggable={false} />
          <span className="font-mono text-[10px] text-gray-500 ml-2 hidden sm:inline uppercase tracking-widest">Admin</span>
        </Link>

        <div className="hidden md:flex items-center gap-6 font-mono text-xs">
          {stats && (
            <>
              <div data-testid="stat-total-opps">
                <span className="text-gray-500 mr-2">OPPS</span>
                <span className="text-gray-900 font-bold">{stats.total_opportunities}</span>
              </div>
              <div data-testid="stat-profitable">
                <span className="text-gray-500 mr-2">PROFITABLE</span>
                <span className="text-[#00A850] font-bold">{stats.profitable}</span>
              </div>
              <div data-testid="stat-threshold">
                <span className="text-gray-500 mr-2">ROI MIN</span>
                <span className="text-[#E3000B] font-bold">{stats.min_roi_threshold}%</span>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/settings"
            data-testid="header-keepa-tokens"
            title={
              tokens.left == null
                ? "Keepa token status unavailable"
                : `${tokens.left} Keepa tokens · refills at ${tokens.refill_rate}/min` +
                  (refillEta ? ` · next refill in ${refillEta}` : "")
            }
            className={`hidden sm:inline-flex items-center gap-1.5 px-2 h-7 border rounded-sm font-mono text-[11px] uppercase tracking-wider transition-colors hover:opacity-80 ${toneCls}`}
          >
            <Coins size={12} weight="fill" />
            <span className="font-bold">{tokens.left == null ? "--" : tokens.left}</span>
            <span className="opacity-70">KEEPA</span>
            {tone === "danger" && refillEta && (
              <span className="opacity-90">· +{refillEta}</span>
            )}
          </Link>
          {/* SerpApi eBay credit pill — pay-per-search, so surface prominently */}
          {ebayCredits.left != null && (
            <span
              data-testid="header-ebay-credits"
              title={`SerpApi eBay: ${ebayCredits.left} searches left this month · ${ebayCredits.cached ?? 0} sets cached (24h)`}
              className={`hidden sm:inline-flex items-center gap-1.5 px-2 h-7 border rounded-sm font-mono text-[11px] uppercase tracking-wider ${
                ebayCredits.left <= 15
                  ? "border-[#FF3333]/40 bg-[#FF3333]/10 text-[#FF3333] animate-pulse"
                  : ebayCredits.left <= 30
                    ? "border-[#FF8C00]/40 bg-[#FF8C00]/10 text-[#FF8C00]"
                    : "border-[#0064D2]/40 bg-[#0064D2]/10 text-[#0064D2]"
              }`}
            >
              <span className="font-bold">{ebayCredits.left}</span>
              <span className="opacity-70">EBAY</span>
            </span>
          )}
          <span className="hidden sm:flex items-center gap-1 text-[10px] font-mono text-[#00A850]">
            <Lightning size={12} weight="fill" /> LIVE
          </span>
          <div className="w-7 h-7 rounded-sm bg-[#F6F6F3] border border-[#E4E4E0] flex items-center justify-center">
            <ChartLineUp size={14} className="text-[#E3000B]" weight="bold" />
          </div>
        </div>
      </div>

      {/* Ticker bar */}
      {ticker.length > 0 && (
        <div className="border-t border-[#E4E4E0] bg-[#F6F6F3] overflow-hidden">
          <div className="flex ticker-track whitespace-nowrap" data-testid="ticker-bar">
            {[...ticker, ...ticker].map((t, i) => {
              const pos = (t.roi_pct || 0) > 0;
              return (
                <div key={i} className="flex items-center gap-2 px-4 py-1 font-mono text-[11px] border-r border-[#E4E4E0]">
                  <span className="text-gray-600">{t.set_number}</span>
                  <span className="text-gray-900 font-medium">{t.name?.substring(0, 18)}</span>
                  <span className={pos ? "text-[#00A850]" : "text-[#FF3333]"}>
                    {pos ? "+" : ""}{(t.roi_pct || 0).toFixed(1)}%
                  </span>
                  <span className="text-gray-500">{fmt(t.net_profit)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
}
